import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Lock, KeyRound, ShieldCheck, EyeOff } from 'lucide-react';

/**
 * "How your data is protected" — landing-page trust block.
 *
 * Plain-English version of the privacy story. Avoids technical jargon
 * (no Fernet, AES-256, JWT, hashing, TLS named). The four cards correspond
 * to the four pillars approved by the founder:
 *   1. Encryption before the database
 *   2. No stored password
 *   3. In-transit + at-rest protection
 *   4. No selling, no sharing, no outside AI tools
 */
const PROTECTIONS = [
  {
    icon: Lock,
    title: 'Sensitive details are encrypted before they reach our database',
    desc:
      'Your name, email, portfolio amounts, account values, and any notes you write are individually scrambled before they ever land in our database. Even our own engineers cannot read them by looking at the raw data.',
    accent: 'bg-emerald-50 text-emerald-700',
    testIdSlug: 'encrypted-before-db',
  },
  {
    icon: KeyRound,
    title: 'We never store your password',
    desc:
      'Sign-in uses a private session that only your browser holds. There is no copy of your original password anywhere on our servers — not encrypted, not hashed in a way we can reverse, not at all.',
    accent: 'bg-blue-50 text-blue-700',
    testIdSlug: 'no-stored-password',
  },
  {
    icon: ShieldCheck,
    title: 'Encrypted in motion, encrypted at rest',
    desc:
      'Every connection between your device and MyInvestFolio is secured end-to-end. Your portfolio data is encrypted before it is stored, and remains encrypted while sitting on our servers.',
    accent: 'bg-slate-100 text-slate-700',
    testIdSlug: 'in-motion-at-rest',
  },
  {
    icon: EyeOff,
    title: 'No selling. No sharing. No outside AI tools.',
    desc:
      'Your portfolio is yours. We do not sell data, we do not share it with advertisers, and we do not feed your private financial details into general-purpose AI tools. The only people who can ever see your numbers are you and the people you choose to share them with.',
    accent: 'bg-purple-50 text-purple-700',
    testIdSlug: 'no-selling-no-sharing',
  },
];

const HowEncryptedSection = () => (
  <section
    id="how-encrypted"
    className="container mx-auto px-4 sm:px-6 py-12 sm:py-24"
    data-testid="how-encrypted-section"
  >
    <div className="text-center space-y-4 mb-8 sm:mb-16">
      <div className="inline-flex flex-col items-center gap-2">
        <div className="subheading">Privacy &amp; Security</div>
        <span aria-hidden className="block w-16 h-0.5 bg-linear-to-r from-emerald-500 to-blue-500 rounded-full"></span>
      </div>
      <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900">
        How your data is <span className="text-emerald-600">protected</span>
      </h2>
      <p className="body-text max-w-2xl mx-auto">
        Your portfolio is private financial information. Here is how we look after it — in plain English, no jargon.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      {PROTECTIONS.map((p) => {
        const Icon = p.icon;
        return (
          <Card key={p.title} className="portfolio-card" data-testid={`protection-card-${p.testIdSlug}`}>
            <CardHeader>
              <div className={`w-12 h-12 ${p.accent.split(' ')[0]} rounded-xl flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${p.accent.split(' ')[1]}`} />
              </div>
              <CardTitle className="text-lg sm:text-xl font-serif">{p.title}</CardTitle>
              <CardDescription className="body-text">{p.desc}</CardDescription>
            </CardHeader>
          </Card>
        );
      })}
    </div>

    <Card className="portfolio-card mt-6 sm:mt-8 bg-slate-50/60 border-slate-200" data-testid="how-encrypted-promise-strip">
      <CardContent className="py-5 sm:py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <ShieldCheck className="w-6 h-6 text-emerald-700 shrink-0" />
          <p className="text-sm sm:text-base text-slate-700">
            <span className="font-semibold text-slate-900">One promise that frames everything else:</span>{' '}
            we treat your portfolio data the way a private bank treats a client file — visible only to the people you authorise, retained only for as long as you keep your account, and never shared without your consent.
          </p>
        </div>
      </CardContent>
    </Card>
  </section>
);

export default HowEncryptedSection;
